interface ContentBlockProps {
 html: string;
 className?: string;
}

function escapeHtml(value: string) {
 return value
 .replaceAll("&", "&amp;")
 .replaceAll("<", "&lt;")
 .replaceAll(">", "&gt;")
 .replaceAll('"', "&quot;")
 .replaceAll("'", "&#39;");
}

function hasHtmlMarkup(value: string) {
 return /<\/?[a-z][\s\S]*>/i.test(value);
}

function formatInlineText(value: string) {
 return escapeHtml(value)
 .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
 .replace(/__(.+?)__/g, "<strong>$1</strong>")
 .replace(/\*(.+?)\*/g, "<em>$1</em>")
 .replace(/_(.+?)_/g, "<em>$1</em>");
}

function isLikelyStandaloneHeading(value: string) {
 const trimmed = value.trim();
 if (!trimmed || trimmed.length > 90) {
 return false;
 }

 if (/[.!?]$/.test(trimmed)) {
 return false;
 }

 return trimmed.split(/\s+/).length <= 10;
}

function formatPlainTextContent(value: string) {
 const normalized = value.replace(/\r\n/g, "\n").trim();
 if (!normalized) {
 return "";
 }

 const blocks = normalized.split(/\n\s*\n+/).map((block) => block.trim()).filter(Boolean);

 return blocks.map((block) => {
 const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
 if (lines.length === 0) {
 return "";
 }

 const markdownHeading = lines[0].match(/^(#{1,6})\s+(.+)$/);
 if (markdownHeading) {
 const level = Math.min(markdownHeading[1].length + 1, 6);
 return `<h${level}>${formatInlineText(markdownHeading[2].trim())}</h${level}>`;
 }

 if (lines.every((line) => /^[-*•]\s+/.test(line))) {
 return `<ul>${lines.map((line) => `<li>${formatInlineText(line.replace(/^[-*•]\s+/, ""))}</li>`).join("")}</ul>`;
 }

 if (lines.every((line) => /^\d+[.)]\s+/.test(line))) {
 return `<ol>${lines.map((line) => `<li>${formatInlineText(line.replace(/^\d+[.)]\s+/, ""))}</li>`).join("")}</ol>`;
 }

 if (lines.length === 1 && isLikelyStandaloneHeading(lines[0])) {
 return `<h3>${formatInlineText(lines[0])}</h3>`;
 }

 if (lines.length > 1 && isLikelyStandaloneHeading(lines[0])) {
 return `<h3>${formatInlineText(lines[0])}</h3><p>${formatInlineText(lines.slice(1).join(" "))}</p>`;
 }

 return `<p>${formatInlineText(lines.join(" "))}</p>`;
 }).join("");
}

function normalizeContentHtml(value: string) {
 const trimmed = value.trim();
 if (!trimmed) {
 return "";
 }

 return hasHtmlMarkup(trimmed) ? trimmed : formatPlainTextContent(trimmed);
}

export function ContentBlock({ html, className }: ContentBlockProps) {
 const normalizedHtml = normalizeContentHtml(html);

 return (
 <div className={`prose prose-lg max-w-none prose-headings:scroll-mt-28 prose-headings:text-foreground prose-p:text-muted-foreground prose-p:leading-8 prose-a:text-blue-600 prose-strong:text-foreground prose-ul:text-muted-foreground prose-ol:text-muted-foreground prose-li:marker:text-primary prose-blockquote:border-primary/30 prose-blockquote:text-muted-foreground ${className ?? ""}`}>
 <div dangerouslySetInnerHTML={{ __html: normalizedHtml }} />
 </div>
 );
}
